import * as React from "react";
import * as m from "~/paraglide/messages";

export type AiProcessingNoticeKind = "cv-intake" | "application";

interface AiProcessingNoticeProps {
  kind?: AiProcessingNoticeKind;
  variant?: "compact" | "panel";
  className?: string;
  id?: string;
}

const bodyByKind: Record<AiProcessingNoticeKind, () => string> = {
  "cv-intake": m.ai_processing_notice_cv_intake_body,
  application: m.ai_processing_notice_application_body,
};

/**
 * Just-in-time EU AI Act Article 50 transparency notice for candidate data flows.
 * Use near controls that trigger CV parsing or application tailoring.
 */
export function AiProcessingNotice({
  kind = "application",
  variant = "panel",
  className,
  id,
}: AiProcessingNoticeProps) {
  const titleId = React.useId();
  const bodyId = React.useId();
  const isCompact = variant === "compact";

  return (
    <aside
      id={id}
      role="note"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      data-compliance="eu-ai-act-art-50"
      data-ai-provider="mistral"
      data-ai-disclosure="candidate-processing"
      className={`text-left ${
        isCompact
          ? "rounded-lg border px-3 py-2 text-xs"
          : "rounded-xl border px-4 py-3"
      } ${className ?? ""}`}
      style={{
        borderColor: "color-mix(in oklch, var(--accent) 28%, var(--border-subtle))",
        background: "color-mix(in oklch, var(--accent) 8%, var(--surface))",
        color: "var(--text)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className={`${isCompact ? "h-6 w-6" : "h-7 w-7"} mt-0.5 flex shrink-0 items-center justify-center rounded-lg`}
          style={{ backgroundColor: "var(--surface)", color: "var(--accent)" }}
          aria-hidden="true"
        >
          <svg
            width={isCompact ? "14" : "15"}
            height={isCompact ? "14" : "15"}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3a6 6 0 0 0-6 6v2a6 6 0 0 0 12 0V9a6 6 0 0 0-6-6Z" />
            <path d="M8 21h8" />
            <path d="M12 17v4" />
            <path d="M9 9h.01" />
            <path d="M15 9h.01" />
            <path d="M10 13h4" />
          </svg>
        </div>
        <div className="min-w-0">
          <p id={titleId} className={`${isCompact ? "text-xs" : "text-sm"} font-semibold text-[var(--text)]`}>
            {m.ai_processing_notice_title()}
          </p>
          <p
            id={bodyId}
            className={`${isCompact ? "mt-0.5 text-xs" : "mt-1 text-xs"} leading-relaxed text-[var(--text-secondary)]`}
          >
            {bodyByKind[kind]()}
          </p>
        </div>
      </div>
    </aside>
  );
}
